import React, { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { Sparkles, Loader2 } from "lucide-react";

export default function Auth({ mode }) {
  const isLogin = mode === "login";
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.from || "/app";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) await login(email, password);
      else await register(name, email, password);
      toast.success(isLogin ? "Welcome back" : "Welcome to Pitch Craft");
      navigate(returnTo);
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = () => { setEmail("demo@lindenai.com"); setPassword("DemoUser2026"); };

  return (
    <div className="min-h-screen grid-bg flex items-center justify-center px-4 py-10" style={{ background: "#F4F7FB" }}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center glow-emerald">
            <Sparkles className="w-5 h-5 text-black" />
          </div>
          <span className="font-display font-extrabold text-xl text-slate-900">Pitch Craft</span>
        </Link>

        <div className="card-surface rounded-2xl p-7">
          <h1 className="text-2xl font-bold text-slate-900 text-center">{isLogin ? "Welcome back" : "Create your account"}</h1>
          <p className="text-sm text-slate-500 text-center mt-1">{isLogin ? "Your coach is waiting." : "Start practicing in seconds."}</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            {!isLogin && (
              <div>
                <Label className="text-slate-600 text-sm">Name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Your name"
                  className="mt-1.5 bg-slate-50 border-slate-200 text-slate-900" data-testid="auth-name-input" />
              </div>
            )}
            <div>
              <Label className="text-slate-600 text-sm">Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@example.com"
                className="mt-1.5 bg-slate-50 border-slate-200 text-slate-900" data-testid="auth-email-input" />
            </div>
            <div>
              <Label className="text-slate-600 text-sm">Password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••"
                className="mt-1.5 bg-slate-50 border-slate-200 text-slate-900" data-testid="auth-password-input" />
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-emerald-500 hover:bg-emerald-600 text-black font-semibold h-11" data-testid="auth-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : isLogin ? "Sign in" : "Create account"}
            </Button>
            {!isLogin && (
              <p className="text-[11px] text-slate-500 text-center leading-relaxed" data-testid="auth-legal-text">
                By creating an account you agree to our <Link to="/terms" className="text-emerald-700 hover:underline" data-testid="auth-terms-link">Terms of Service</Link> and <Link to="/privacy" className="text-emerald-700 hover:underline" data-testid="auth-privacy-link">Privacy Policy</Link>.
              </p>
            )}
          </form>

          {isLogin && (
            <button onClick={fillDemo} className="w-full mt-3 text-xs text-emerald-600 hover:text-emerald-800 font-mono" data-testid="demo-fill-btn">
              Use demo account
            </button>
          )}

          <p className="text-sm text-slate-500 text-center mt-6">
            {isLogin ? "New here? " : "Already have an account? "}
            <Link to={isLogin ? "/register" : "/login"} className="text-emerald-600 hover:text-emerald-800 font-medium" data-testid="auth-switch-link">
              {isLogin ? "Create an account" : "Sign in"}
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
}
